import axios from 'axios'

import Portico from '@ordernext/networking-stadium/portico'
import Stadium from '@ordernext/networking-stadium/stadium'

import createDataDogLog from '../utils/datadog'
import { ToastManager } from '../utils/toastManager'
import { camelizeKeys, decamelizeKeys } from 'humps'
import { get, some } from 'lodash'

import { injectNonDecamelizedPaymentsKeysForPpi } from '../VNPPI/Utils'
import { isEmpty } from '../VNUtil/VNUtils'

// Do not toast errors received from these endpoints
const STADIUM_ENDPOINT_ERROR_BLACKLIST = ['/gift_cards', '/tickets', '/nonces']
const ERROR_EXCLUSION_LIST = ['Could not find such an attendant!', 'Nonce charge failed.', 'Wallet charge failed.']

class BaseRemote {
  constructor(baseUrl) {
    let fetchingTokens = false

    const instance = axios.create({
      transformResponse: [...axios.defaults.transformResponse, camelizeKeys],
    })

    instance.interceptors.request.use(
      (config) => {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Cache-Control': 'no-cache, no-store',
          Pragma: 'no-cache',
          Expires: 0,
        }

        let deepCopy = {}
        Object.assign(deepCopy, config?.data)

        config.baseURL = baseUrl
        config.params = decamelizeKeys(config?.params ?? {})
        config.data = decamelizeKeys(config?.data ?? {})

        // not every parameter that we send to stadium needs to be decamelized
        // these variables aren't read by stadium specifically, but are
        // passed around to VNAPI and POS for reading.
        injectNonDecamelizedPaymentsKeysForPpi(deepCopy, config.data)

        // LAVA is being sent as l_a_v_a this fixes that
        if (config?.data?.user_attributes?.ppi_extras && deepCopy?.userAttributes?.ppiExtras) {
          config.data.user_attributes.ppiExtras = deepCopy?.userAttributes?.ppiExtras
          delete config?.data?.user_attributes?.ppi_extras
        }

        // this keeps the camel case for orderMutations that have ppi extras
        // ppi extras are fed through to VNAPI which uses camelCase
        const ppiExtras = deepCopy.orderMutations?.[0]?.mutationPayload?.payment?.userAttributes?.ppiExtras
        if (!isEmpty(ppiExtras)) {
          config.data.order_mutations[0].mutation_payload.payment.user_attributes.ppiExtras = ppiExtras
          delete config.data.order_mutations?.[0]?.mutation_payload?.payment?.user_attributes?.ppi_extras
        }

        return config
      },
      (error) => Promise.reject(error),
    )

    const handleBadToken = async (config) => {
      fetchingTokens = true
      config.sent = true

      const deviceId = window.localStorage.device_id
      const appKey = process.env.REACT_APP_OIDC_CLIENT_ID
      const organizationName = process.env.REACT_APP_ORG
      let response = { data: {} }

      try {
        response = await Portico.deviceLogin(deviceId, appKey, organizationName)
      } catch (error) {
        fetchingTokens = false
        return Promise.reject(error)
      }

      const accessToken = get(response, 'data.accessToken', undefined)
      const expiresAt = get(response, 'data.expiresAt', undefined)

      if (accessToken && expiresAt) {
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('expiresAt', expiresAt)

        Stadium.config = {
          ...Stadium.config,
          headers: {
            ...Stadium.config.headers,
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache, no-store',
            Pragma: 'no-cache',
            Expires: 0,
          },
        }
      }

      fetchingTokens = false
      config.headers.Authorization = `Bearer ${localStorage.getItem('accessToken')}`

      return axios(config)
    }

    const reportableIssue = (error) => {
      const response = error?.response

      const applicationMessage = response?.data?.errorMessage
      const friendlyMessage = response?.data?.friendlyMessage
      const errorMessage = friendlyMessage ?? applicationMessage

      return {
        shouldLog: errorMessage && error?.message !== 'Network Error' && !ERROR_EXCLUSION_LIST.includes(applicationMessage),
        shouldToast: !some(STADIUM_ENDPOINT_ERROR_BLACKLIST, (endpoint) => response?.config?.url?.includes(endpoint)),
        errorMessage: (friendlyMessage || errorMessage)?.slice(0, 100),
      }
    }

    instance.interceptors.response.use(
      (response) => {
        return response
      },
      async (err) => {
        const response = err?.response
        const config = err?.config

        if (response?.status === 401 && response?.data?.error === 'invalid_token' && !config?.sent && !fetchingTokens) {
          return await handleBadToken(config)
        }

        const reportable = reportableIssue(err)

        if (reportable.shouldToast && reportable.errorMessage) {
          ToastManager.error(`${reportable.errorMessage}`, { autoClose: true })
        }

        if (reportable.shouldLog) {
          createDataDogLog('error', reportable.errorMessage)
        }

        return Promise.reject(err)
      },
    )

    return instance
  }
}

export default BaseRemote
